import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import classNames from 'classnames';

import { IPaginationResponse } from 'api/resort/types';
import { ILiftTicketData, ILiftTicketsResponse } from 'components/region/LiftTicketTable/types';
import { HeaderClickEvent, IGridColumn, IGridSorting } from 'components/common/Grid/types';

import { WEATHER_PAGE_SIZE } from 'ots-constants';
import { Diagnostics } from 'util/diagnostics';
import { fetchRegionLiftTicketsPricesByResort } from 'api/region/static';
import { useOTSTranslation } from 'i18n/useOTSTranslation';

import { GridLoadable } from 'components/common/Grid/GridLoadable';
import { getTableColumns } from 'components/region/LiftTicketTable/columns';

import styles from './styles.module.scss';
import { SortingKeys } from './sortingKeys';

export interface IProps {
  resortsPricesPreloaded: ILiftTicketsResponse;
  regionId: string;
  hasMore: boolean,
  isFresh: boolean;
}

export const LiftTicketTable = ({
  hasMore,
  isFresh,
  regionId,
  resortsPricesPreloaded,
}: IProps) => {
  const { t } = useOTSTranslation('common');
  const [resortPricesList, setResortPricesList] = useState<Array<ILiftTicketData>>(resortsPricesPreloaded.data);
  const [pagination, setPagination] = useState<IPaginationResponse>(resortsPricesPreloaded.pagination);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadMore, setLoadMore] = useState<boolean>(hasMore);
  const [gridSorting, setGridSorting] = useState<IGridSorting>();

  const loadNextPage = useCallback(async () => {
    setIsLoading(true);
    const nextPage = (pagination?.page ?? 0) + 1;

    try {
      const data = await fetchRegionLiftTicketsPricesByResort(
        regionId,
        nextPage,
        WEATHER_PAGE_SIZE,
        gridSorting?.orderBy,
        gridSorting?.direction,
      );

      const updatedResortList = [...resortPricesList, ...data.data];
      setResortPricesList(updatedResortList);
      setPagination(data.pagination);

      setLoadMore(data.pagination.count > updatedResortList.length);
      setIsLoading(false);
    } catch (e: any) {
      Diagnostics.error({
        tag: 'region-resorts',
        error: e,
        message: `Failed to load next page for region ${regionId}`,
        data: { regionId, nextPage },
      });
      setIsLoading(false);
    }
  }, [resortPricesList, pagination, gridSorting]);

  const onSortingChange = useCallback(async (
    event: HeaderClickEvent, column: IGridColumn, sorting?: IGridSorting,
  ) => {
    setIsLoading(true);
    try {
      const orderBy = sorting?.orderBy as keyof typeof SortingKeys;
      const sortingKey = orderBy ? SortingKeys[orderBy] : undefined;

      const response = await fetchRegionLiftTicketsPricesByResort(
        regionId,
        0,
        WEATHER_PAGE_SIZE,
        sortingKey,
        sorting?.direction,
      );

      setResortPricesList(response.data);
      setPagination(response.pagination);
      setLoadMore(response.pagination.count > response.data.length);
      setGridSorting({
        direction: sorting?.direction,
        orderBy: sortingKey,
      });
    } catch (e: any) {
      Diagnostics.error({
        tag: 'region-lift-tickets',
        error: e,
        message: `Failed to load lift-tickets next page for region ${regionId}`,
        data: { regionId, nextPage: pagination?.page },
      });
    }
    setIsLoading(false);
  }, []);

  const allPricesInOneCurrency = useMemo<string | null>(
    () => {
      const commonCurrency = resortPricesList.reduce(
        (acc, item) => {
          if (acc === '') {
            return item.currency;
          }
          if (acc !== '' && acc === item.currency) {
            return acc;
          }
          return null;
        },
        '' as string | null,
      );

      return commonCurrency;
    },
    [resortPricesList],
  );

  const hasPurchases = useMemo(() => resortPricesList.some((i) => !!i.purchaseUrl), [resortPricesList]);

  useEffect(() => {
    setResortPricesList(resortsPricesPreloaded.data);
    setPagination(resortsPricesPreloaded.pagination);
  }, [resortsPricesPreloaded]);

  return (
    <>
      {allPricesInOneCurrency && (
        <div className={styles.topNotice}>
          {`${t('resort.liftTickets.currencyText')} ${allPricesInOneCurrency}`}
        </div>
      )}
      <GridLoadable
        data={resortPricesList}
        columns={getTableColumns(!!allPricesInOneCurrency)}
        setIsLoading={setIsLoading}
        onSortingChange={onSortingChange}
        loadNextPage={loadNextPage}
        isFresh={isFresh}
        isLoading={isLoading}
        isLoadMoreAvailable={hasLoadMore}
        total={pagination?.count || 0}
        noValueSymbol={t('common.dash')}
        className={classNames(!hasPurchases && styles.withoutPurchasing)}
      />
      <div className={styles.bottomNotice}>
        {t('region.liftTickets.disclaimer')}
      </div>
    </>
  );
};
